import { useEffect, useRef, useState } from 'react'
import { PageBootSkeleton } from '@/components/layout/PageBootSkeleton'
import { ModuleAccessState } from '@/components/shared/empty-states/AccessState'
import { useMyHostOrgsQuery } from '@/hooks/queries/useExternalOrgsQuery'
import {
  ExtendedOrganization,
  useOrganizationsQuery,
} from '@/hooks/queries/useOrganizationsQuery'
import { useOrganizationSubscription } from '@/hooks/useOrganizationSubscription'
import { dismissBootSplash } from '@/lib/boot-splash'
import { syncAccountTypeTheme } from '@/lib/personal-theme'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { useUIPreferencesStore } from '@/store/ui-preferences.store'
import { useUserProfileStore } from '@/store/user-profile.store'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

/**
 * Component that guards organization-specific routes
 * - Fetches organizations if not loaded
 * - Validates org slug exists and user has access
 * - Checks if organization has active subscription (redirects to subscription page if needed)
 * - Handles admin route access control
 * - Manages admin mode UI state
 * - Sets the current organization in the store
 */
export function OrgRouteGuard() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const previousSlugRef = useRef<string | null>(null)
  // Track which org we've already validated subscription for (to avoid repeated redirects)
  const subscriptionValidatedRef = useRef<string | null>(null)
  const previousOrgIdRef = useRef<string | null>(null)

  const { setCurrentOrganizationFromSlug, currentOrganization } =
    useOrganizationStore()
  const { isAuthenticated } = useAuth()

  // Use React Query to fetch organizations - it handles deduplication internally
  // MainLayout pre-fetches this, so it will usually be cached already
  const { data: organizations = [], isPending: isLoading } =
    useOrganizationsQuery({
      enabled: isAuthenticated,
    })

  // Also fetch external orgs (host organizations the user can access)
  // We need isFetched to ensure we don't make access decisions before the query completes
  const {
    data: myHostOrgs = [],
    isPending: isHostOrgsLoading,
    isFetched: isHostOrgsFetched,
  } = useMyHostOrgsQuery()

  const { resetOrganizationData } = useUserProfileStore()
  const isAdminRoute = location.pathname.includes('/admin')
  const isIntegrationsAdminRoute = location.pathname.includes(
    '/admin/integrations',
  )
  const isSubscriptionRoute = location.pathname.includes('/subscription')
  const [isCheckingAdminAccess, setIsCheckingAdminAccess] = useState(false)
  // A member on a role-gated admin route: the guard keeps the org primed and
  // renders the one access state in place instead of bouncing to Today
  // (design audit S19). Admin mode (the admin sidebar) only switches on once
  // access is confirmed, so the sidebar's "lost admin access" exit never
  // fires for a member who never had it.
  const [adminAccess, setAdminAccess] = useState<
    'unknown' | 'granted' | 'denied'
  >('unknown')
  const isAdminDenied = adminAccess === 'denied'

  // Check subscription status for the current organization
  // Only check when authenticated AND we have an org ID to avoid race conditions
  const {
    needsSubscription,
    isLoading: isSubLoading,
    isFetched: isSubFetched,
    error: subscriptionError,
  } = useOrganizationSubscription(currentOrganization?.id, {
    enabled: isAuthenticated && !!currentOrganization?.id,
  })

  // Manage admin mode UI state
  useEffect(() => {
    const uiPrefs = useUIPreferencesStore.getState()
    const wantsAdminMode = isAdminRoute && adminAccess === 'granted'
    if (wantsAdminMode && !uiPrefs.isAdminMode) {
      uiPrefs.setAdminMode(true)
    } else if (!isAdminRoute && uiPrefs.isAdminMode) {
      uiPrefs.setAdminMode(false)
    }
  }, [isAdminRoute, adminAccess])

  // Reset user profile when switching organizations
  useEffect(() => {
    if (orgSlug && orgSlug !== previousSlugRef.current) {
      resetOrganizationData()
      previousSlugRef.current = orgSlug
    }
  }, [orgSlug, resetOrganizationData])

  const checkOrganizationAccess = (
    orgs: typeof organizations,
    slug: string,
    hostOrgs: typeof myHostOrgs,
  ) => {
    // Check if current organization is already set correctly (e.g., from organizations page click)
    // This prevents race conditions where the org is already set but we're still checking
    const currentOrgState = useOrganizationStore.getState().currentOrganization
    if (currentOrgState?.slug === slug) {
      // If it's already set as external access, trust that and don't re-check
      if (currentOrgState.isExternalAccess) {
        // For admin routes, external org users don't have admin access
        if (isAdminRoute) {
          toast.error("External organization users don't have admin privileges")
          navigate(`/${slug}/today`, { replace: true })
        }
        return
      }
      // If it's a direct org that's already set, also skip
      const matchingOrg = orgs.find(org => org.slug === slug)
      if (matchingOrg?.userMembership) {
        return
      }
    }

    // Find the organization in direct memberships
    const organization = orgs.find(org => org.slug === slug)

    // If not found in direct orgs, check if it's an external org the user can access
    const externalOrgRelationship = hostOrgs.find(
      rel => rel.hostOrganization?.slug === slug,
    )

    if (!organization && !externalOrgRelationship) {
      navigate('/organizations', { replace: true })
      return
    }

    // Determine the best access path:
    // 1. If org has valid ACTIVE direct membership (userMembership exists and is not suspended/inactive) -> direct membership flow
    // 2. Else if user has external org access -> external org flow (mirrors backend AccessGuard which allows
    //    external access even when direct membership is suspended/inactive)
    // 3. Else -> no access

    const directMembership = organization?.userMembership
    const hasActiveDirectMembership =
      !!directMembership &&
      directMembership.status !== 'SUSPENDED' &&
      directMembership.status !== 'INACTIVE'

    // External org flow: take this path when there is no active direct membership but external access exists.
    // This intentionally fires for users whose direct membership is suspended/inactive but who still have
    // valid external-org access — same as backend AccessGuard behaviour.
    if (externalOrgRelationship && !hasActiveDirectMembership) {
      const hostOrg = externalOrgRelationship.hostOrganization
      if (hostOrg) {
        // For admin routes, external org users don't have admin access
        if (isAdminRoute) {
          toast.error("External organization users don't have admin privileges")
          navigate(`/${slug}/today`, { replace: true })
          return
        }

        // Set the external organization as current with the isExternalAccess flag
        // Also pass the external relationship for permission checking
        useOrganizationStore.getState().setCurrentOrganization(
          {
            id: hostOrg.id,
            name: hostOrg.name,
            slug: hostOrg.slug,
            logo: hostOrg.logo || null,
            isExternalAccess: true,
          } as ExtendedOrganization,
          externalOrgRelationship,
        )
      }
      return
    }

    // Direct membership flow
    if (organization && directMembership) {
      const membership = organization.userMembership!

      // Check if user is suspended or inactive
      if (membership.status === 'SUSPENDED') {
        toast.error(
          'Your access to this organization has been suspended. Redirecting to organizations page...',
        )
        // Clear current organization to prevent API calls with org headers
        setCurrentOrganizationFromSlug(null, orgs)
        navigate('/organizations?suspended=true', { replace: true })
        return
      }

      if (membership.status === 'INACTIVE') {
        toast.error(
          'Your account is inactive in this organization. Please contact an administrator.',
        )
        // Clear current organization to prevent API calls with org headers
        setCurrentOrganizationFromSlug(null, orgs)
        navigate('/organizations?suspended=true', { replace: true })
        return
      }

      // Most admin routes remain role-based, but integrations also supports
      // member access when the member's permission set grants integration access.
      if (isAdminRoute) {
        setIsCheckingAdminAccess(true)
        const hasAdminAccess =
          membership.role === 'ADMIN' || membership.role === 'OWNER'
        setIsCheckingAdminAccess(false)
        setAdminAccess(
          !hasAdminAccess && !isIntegrationsAdminRoute ? 'denied' : 'granted',
        )
      } else {
        setAdminAccess('unknown')
      }

      // Set the current organization
      setCurrentOrganizationFromSlug(slug, orgs)
      return
    }

    // Fallback: Organization was found but user has no valid access (no membership, no external org access)
    // This could happen if the org was in the list due to portal access but user doesn't have the right permissions
    toast.error("You don't have access to this organization")
    navigate('/organizations', { replace: true })
  }

  // Desktop boot splash handoff: hold the splash through the org-priming
  // phase and release it only when the destination page is about to render
  // (same condition as the <Outlet /> branch below). The page's own
  // in-layout skeleton is what gets revealed, so boot goes
  // splash → page skeleton → content with no intermediate loader stack.
  const isOrgReady =
    !!orgSlug &&
    !isLoading &&
    !isHostOrgsLoading &&
    isHostOrgsFetched &&
    !isCheckingAdminAccess &&
    currentOrganization?.slug === orgSlug
  useEffect(() => {
    if (isOrgReady) {
      dismissBootSplash({ waitForGlass: true })
    }
  }, [isOrgReady])

  // Authoritative warm-hearth re-stamp: once the store holds the org that
  // matches the URL, re-derive the personal theme from the org actually being
  // viewed. The personal-theme store subscription only fires on store
  // transitions, which this window may never see (it can boot straight into the
  // target org from the synchronous account-type cache, or be primed from a
  // stale currentOrganizationId / a partial org object). Deriving from the
  // URL-resolved org here guarantees personal → work reverts on every path —
  // direct URL, new tab, desktop window — and rewrites the shared cache so
  // popup windows following via the `storage` event get the corrected value.
  useEffect(() => {
    if (currentOrganization?.slug === orgSlug) {
      syncAccountTypeTheme(currentOrganization?.accountType)
    }
  }, [orgSlug, currentOrganization?.slug, currentOrganization?.accountType])

  // Reset subscription validation ref when organization changes
  useEffect(() => {
    const currentOrgId = currentOrganization?.id || null
    const previousOrgId = previousOrgIdRef.current

    // If organization changed, reset subscription validation
    if (previousOrgId !== null && previousOrgId !== currentOrgId) {
      subscriptionValidatedRef.current = null
    }

    previousOrgIdRef.current = currentOrgId
  }, [currentOrganization?.id])

  // Separate effect to handle subscription checking after organization is set
  // SIMPLIFIED LOGIC using isFetched flag:
  // - Only redirect when isFetched=true AND needsSubscription=true
  // - If there's an error, isFetched will be false, so we won't redirect
  // - This prevents false redirects on network errors or auth issues
  useEffect(() => {
    const isSubscriptionSetupRoute = location.pathname.includes(
      '/subscription-setup',
    )

    // Skip subscription check in these cases:
    if (
      !currentOrganization ||
      !orgSlug ||
      isSubscriptionRoute ||
      isSubscriptionSetupRoute ||
      isAdminRoute
    ) {
      return
    }

    const currentOrgId = currentOrganization.id
    const currentOrgSlug = currentOrganization.slug

    // CRITICAL: Ensure org slug from URL matches the current organization slug
    // This prevents checking subscription during org transition when IDs/slugs are mismatched
    if (orgSlug !== currentOrgSlug) {
      return
    }

    // Still loading - wait
    if (isSubLoading) {
      return
    }

    // CRITICAL: Only make redirect decision when we have definitively fetched the subscription status
    // isFetched is TRUE only when:
    // - API call succeeded (subscription exists OR 404 returned)
    // isFetched is FALSE when:
    // - Still loading
    // - Error occurred (network error, 403, etc.)
    // - Hook is disabled (waiting for auth)
    if (!isSubFetched) {
      return
    }

    // At this point, isFetched=true means we have a definitive answer
    // Only validate once per organization to avoid repeated redirects
    if (subscriptionValidatedRef.current !== currentOrgId) {
      subscriptionValidatedRef.current = currentOrgId

      if (needsSubscription) {
        navigate(`/${currentOrgSlug}/subscription-setup`, { replace: true })
      }
    }
  }, [
    orgSlug,
    currentOrganization?.id,
    currentOrganization?.slug,
    isSubscriptionRoute,
    location.pathname,
    isSubLoading,
    isSubFetched,
    isAdminRoute,
    needsSubscription,
    subscriptionError,
    navigate,
  ])

  useEffect(() => {
    // If no org slug in URL, redirect to organizations page
    if (!orgSlug) {
      navigate('/organizations', { replace: true })
      return
    }

    // If organizations or host orgs are still loading, wait
    if (isLoading || isHostOrgsLoading) {
      return
    }

    // CRITICAL: Wait for external orgs query to complete before making access decisions
    // This prevents false redirects when the user has external org access but the query
    // hasn't finished yet (e.g., on page refresh where direct orgs may be cached)
    if (!isHostOrgsFetched) {
      return
    }

    // Organizations are automatically fetched by useOrganizationsQuery
    // If organizations are loaded, check access (including external orgs)
    if (organizations.length > 0 || myHostOrgs.length > 0) {
      checkOrganizationAccess(organizations, orgSlug, myHostOrgs)
    } else if (!isLoading && !isHostOrgsLoading) {
      // If not loading and no organizations (direct or external), redirect to organizations page
      navigate('/organizations', { replace: true })
    }
    // Depend on actual props, not function closure that's fresh every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orgSlug,
    organizations,
    myHostOrgs,
    navigate,
    isLoading,
    isHostOrgsLoading,
    isHostOrgsFetched,
    isAdminRoute,
  ])

  // Don't render anything if no org slug (will redirect)
  if (!orgSlug) {
    return null
  }

  // Show loading state while organizations are loading or checking admin access
  // Also wait for external orgs to be fetched before rendering
  // But allow effects to run even during loading
  if (
    isLoading ||
    isHostOrgsLoading ||
    !isHostOrgsFetched ||
    isCheckingAdminAccess
  ) {
    return <PageBootSkeleton />
  }

  // If organizations are loaded but empty (both direct and external), we should have already redirected
  // But allow effects to run to handle the redirect
  if (organizations.length === 0 && myHostOrgs.length === 0) {
    return null
  }

  // Wait until the org store has been primed with the org matching the URL slug.
  // Otherwise child guards (e.g. FeatureFlagGuard) can evaluate against a null
  // or stale currentOrganization and redirect incorrectly.
  if (currentOrganization?.slug !== orgSlug) {
    return <PageBootSkeleton />
  }

  if (isAdminRoute && isAdminDenied) {
    return <ModuleAccessState moduleSlug="admin" title="Admin" />
  }

  return <Outlet />
}
